/**
 * Plukkstatistikk — TW 36333674 / 36333675
 *
 * Egen visningstype i Statistikk-modulen. Viser plukkeffektivitet og dødtid per
 * operatør, vektede totaler for hele lageret, og trendserier.
 *
 * Spesifikasjon: "Claude md files/Operatorstatistikk-Spesifikasjon.md"
 *
 * Tre ting som er lette å gjøre feil, og som derfor er løst bevisst her:
 *  1. Totalene er VEKTET av backend (sum/sum), ikke snittet av kolonnen.
 *  2. Trendretningen er regresjonsbasert, ikke første mot siste punkt.
 *  3. Dødtid uten målinger vises blank — ikke 0, som ville lest som «ingen dødtid».
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine
} from 'recharts';
import { RefreshCw, AlertCircle, Download, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import statisticsService from '../../../../services/statisticsService';
import {
  PickingStatisticsResponse,
  PickingStatisticsRow,
  TrendInterval
} from '../../../../types/statistics';

interface PickingStatisticsViewProps {
  startDate: Date;
  endDate: Date;
}

type SortKey = keyof Pick<
  PickingStatisticsRow,
  'operatorName' | 'lines' | 'orders' | 'quantityPicked' | 'averageMinutesPerLine'
  | 'linesPerHour' | 'unitsPerHour' | 'pickingHours' | 'interruptedHours'
  | 'averageDeadTimeMinutes' | 'trendPercent'
>;

const num = (v: number, d = 0) =>
  v.toLocaleString('nb-NO', { minimumFractionDigits: d, maximumFractionDigits: d });

/** Liten kurve i tabellraden. Viser faktiske punkter; retningen står som prosent ved siden av. */
const Sparkline: React.FC<{ series: number[]; percent: number | null | undefined }> = ({ series, percent }) => {
  const { t } = useTranslation();

  // == null fanger både null og undefined — API-et utelater null-felter
  // (WhenWritingNull i Program.cs), så feltet mangler helt i JSON.
  if (series.length < 4 || percent == null) {
    return (
      <span className="text-xs text-gray-400">
        {t('manager.modules.statistics.picking.seriesTooShort', {}, 'for kort serie')}
      </span>
    );
  }

  const w = 64;
  const h = 20;
  const min = Math.min(...series);
  const max = Math.max(...series);
  const range = max - min || 1;
  const points = series
    .map((v, i) => `${2 + ((w - 4) * i) / (series.length - 1)},${h - 3 - ((h - 6) * (v - min)) / range}`)
    .join(' ');

  const rising = percent >= 6;
  const falling = percent <= -6;
  const stroke = rising ? '#15803d' : falling ? '#b91c1c' : '#94a3b8';
  const textClass = rising ? 'text-green-700' : falling ? 'text-red-700' : 'text-gray-500';
  const arrow = percent > 0 ? '▲ +' : percent < 0 ? '▼ −' : '– ';

  return (
    <span className="inline-flex items-center justify-end gap-2">
      <svg width={w} height={h} aria-hidden="true">
        <polyline points={points} fill="none" stroke={stroke} strokeWidth={1.7} strokeLinejoin="round" />
      </svg>
      <span className={`text-xs font-semibold tabular-nums ${textClass}`}>
        {arrow}{num(Math.abs(percent), 0)} %
      </span>
    </span>
  );
};

export const PickingStatisticsView: React.FC<PickingStatisticsViewProps> = ({ startDate, endDate }) => {
  const { t } = useTranslation();

  const [data, setData] = useState<PickingStatisticsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [interval, setInterval] = useState<TrendInterval>(1);
  const [zones, setZones] = useState<string[]>([]);
  const [showInfo, setShowInfo] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('linesPerHour');
  const [sortAsc, setSortAsc] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await statisticsService.getPickingStatistics(startDate, endDate, {
        interval,
        zones,
        operators: []
      });
      setData(result);
    } catch (err) {
      setError(err instanceof Error
        ? err.message
        : t('manager.modules.statistics.errors.fetchFailed', {}, 'Kunne ikke hente statistikk'));
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, interval, zones, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sortedRows = useMemo(() => {
    if (!data) return [];
    return [...data.rows].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      // null/undefined (mangler data) sorteres alltid sist, uansett retning
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string' && typeof bv === 'string') {
        return sortAsc ? av.localeCompare(bv, 'nb-NO') : bv.localeCompare(av, 'nb-NO');
      }
      return sortAsc ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });
  }, [data, sortKey, sortAsc]);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(false);
    }
  };

  const handleExport = () => {
    if (!data || data.rows.length === 0) return;
    const flat = data.rows.map(r => ({
      Operator: r.operatorName,
      Linjer: r.lines,
      Ordrer: r.orders,
      PlukketAntall: r.quantityPicked,
      SnittPlukktidMin: r.averageMinutesPerLine,
      LinjerPerTime: r.linesPerHour,
      EnheterPerTime: r.unitsPerHour,
      Plukktimer: r.pickingHours,
      AvbruttTimer: r.interruptedHours,
      SnittDodtidMin: r.averageDeadTimeMinutes ?? '',
      DodtidMalinger: r.deadTimeSamples,
      TrendProsent: r.trendPercent ?? ''
    }));
    const dateStr = new Date().toISOString().split('T')[0];
    statisticsService.exportToCSV(flat, `plukkstatistikk-${dateStr}.csv`);
  };

  const chartData = useMemo(() => (data?.trend ?? []).map(p => ({
    label: p.label,
    lines: p.lines,
    lph: p.linesPerHour
  })), [data]);

  // Raden med flest linjer brukes som levende regneeksempel i forklaringspanelet —
  // ett ekte regnestykke fra egne data forklarer mer enn ti abstrakte formler.
  const exampleRow = useMemo(() => {
    if (!data || data.rows.length === 0) return null;
    return [...data.rows].sort((a, b) => b.lines - a.lines).find(r => r.pickingHours > 0) ?? null;
  }, [data]);

  const breakLabels = useMemo(() => {
    if (!data) return [];
    // Kodeskiftene markeres på det trendpunktet som inneholder bruddet.
    return data.identityBreaks
      .map(b => {
        const bd = new Date(b);
        const hit = [...data.trend].reverse().find(p => new Date(p.periodStart) <= bd);
        return hit?.label;
      })
      .filter((l): l is string => !!l);
  }, [data]);

  const th = (key: SortKey, label: string, extra = '') => (
    <th
      onClick={() => handleSort(key)}
      className={`px-3 py-2 text-xs font-semibold text-white whitespace-nowrap cursor-pointer hover:bg-blue-700 ${extra}`}
    >
      {label}
      {sortKey === key && <span className="ml-1">{sortAsc ? '▲' : '▼'}</span>}
    </th>
  );

  return (
    <div className="space-y-6">
      {/* Forklaring — samme mønster som «Hva er Nye Ordre?»: informasjonen må
          søkes fram eksplisitt, den skal ikke stå i veien for tallene */}
      <div>
        <button
          type="button"
          onClick={() => setShowInfo(prev => !prev)}
          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 focus:outline-none"
          aria-expanded={showInfo}
        >
          <Info size={14} />
          <span>{t('manager.modules.statistics.picking.info.toggle', {}, 'Hvordan beregnes tallene?')}</span>
          {showInfo ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>

        {showInfo && (
          <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-gray-800">
            <div className="mb-3">
              <h3 className="font-bold text-gray-900 mb-1">
                {t('manager.modules.statistics.picking.info.purposeHeading', {}, 'Hensikt')}
              </h3>
              <p>
                {t('manager.modules.statistics.picking.info.purposeBody', {},
                  'Plukkstatistikken viser hvor mye og hvor raskt hver operatør plukker, basert på tiden fra en plukklinje startes til den bekreftes. Tallene er ment som grunnlag for å se utvikling og fordele ressurser — ikke som fasit på enkeltpersoner uten kontekst.')}
              </p>
            </div>

            <div className="mb-3">
              <h3 className="font-bold text-gray-900 mb-1">
                {t('manager.modules.statistics.picking.info.basisHeading', {}, 'Hvilke linjer teller med')}
              </h3>
              <p className="mb-1">
                {t('manager.modules.statistics.picking.info.basisIntro', {},
                  'En plukklinje teller med når den er fullført med antall og operatør, og startet og fullført samme dag. Deretter gjelder to grenser:')}
              </p>
              <ul className="list-disc pl-5 space-y-0.5">
                <li>
                  {t('manager.modules.statistics.picking.info.basisWork',
                    { '0': String(data?.parameters.workLimitMinutes ?? 15) },
                    `Arbeidsgrense (${data?.parameters.workLimitMinutes ?? 15} min): linjer som tar lengre tid regnes som avbrutt arbeid (telefon, avvik, annet) og holdes utenfor alle snitt — ellers ville én glemt linje ødelagt hele snittet. Tiden vises i stedet i kolonnen «Avbrutt».`)}
                </li>
                <li>
                  {t('manager.modules.statistics.picking.info.basisUpper',
                    { '0': String(data?.parameters.upperLimitMinutes ?? 120) },
                    `Øvre grense (${data?.parameters.upperLimitMinutes ?? 120} min): linjer over denne forkastes helt og teller ikke noe sted.`)}
                </li>
              </ul>
              <p className="mt-1">
                {t('manager.modules.statistics.picking.info.basisNote', {},
                  'Grunnlagsstripen over tabellen viser alltid hvilke grenser som er brukt, og hvor mange linjer som falt utenfor.')}
              </p>
            </div>

            <div className="mb-3">
              <h3 className="font-bold text-gray-900 mb-1">
                {t('manager.modules.statistics.picking.info.columnsHeading', {}, 'Slik beregnes kolonnene')}
              </h3>
              <ul className="list-disc pl-5 space-y-0.5">
                <li>
                  {t('manager.modules.statistics.picking.info.colAvgMin', {},
                    'Snitt plukktid = sum plukktid delt på antall linjer.')}
                </li>
                <li>
                  {t('manager.modules.statistics.picking.info.colLph', {},
                    'Linjer/time = antall linjer delt på plukktimer.')}
                </li>
                <li>
                  {t('manager.modules.statistics.picking.info.colUph', {},
                    'Enheter/time = plukket antall delt på plukktimer. Kan ikke justeres for vekt eller størrelse på varene, og er derfor en volumindikator — ikke et effektivitetsmål.')}
                </li>
                <li>
                  {t('manager.modules.statistics.picking.info.colDeadTime',
                    { '0': String(data?.parameters.deadTimeGapMinutes ?? 30) },
                    `Snitt dødtid = gjennomsnittlig opphold fra en plukkliste fullføres til operatøren starter neste. Bare opphold på inntil ${data?.parameters.deadTimeGapMinutes ?? 30} minutter teller — lengre opphold regnes som pause, lunsj eller møte. «n=» viser hvor mange opphold snittet bygger på.`)}
                </li>
                <li>
                  {t('manager.modules.statistics.picking.info.colTrend', {},
                    'Trend = retningen på en tilpasset linje gjennom hele perioden — ikke bare første mot siste punkt, som svinger for mye. Krever minst 4 punkter i serien.')}
                </li>
                <li>
                  {t('manager.modules.statistics.picking.info.colTotals', {},
                    'Totalraden er vektet: sum linjer delt på sum timer for hele lageret — ikke gjennomsnittet av kolonnen. Dermed teller en operatør med mange timer mer enn en med få.')}
                </li>
              </ul>
            </div>

            {exampleRow && (
              <div className="mb-3">
                <h3 className="font-bold text-gray-900 mb-1">
                  {t('manager.modules.statistics.picking.info.exampleHeading', {}, 'Regneeksempel fra denne perioden')}
                </h3>
                <p>
                  {t('manager.modules.statistics.picking.info.exampleBody',
                    {
                      '0': exampleRow.operatorName,
                      '1': num(exampleRow.lines),
                      '2': num(exampleRow.pickingHours, 1),
                      '3': num(exampleRow.linesPerHour, 1)
                    },
                    `${exampleRow.operatorName} plukket ${num(exampleRow.lines)} linjer på ${num(exampleRow.pickingHours, 1)} plukktimer → ${num(exampleRow.linesPerHour, 1)} linjer/time.`)}
                </p>
              </div>
            )}

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
              <h3 className="font-bold text-gray-900 mb-1">
                {t('manager.modules.statistics.picking.info.cautionHeading', {}, 'Pass på')}
              </h3>
              <ul className="list-disc pl-5 space-y-0.5 text-gray-800">
                <li>
                  {t('manager.modules.statistics.picking.info.cautionZones', {},
                    'Operatørene rullerer mellom soner, og plukktiden varierer mellom sonene. Sammenlign innenfor samme sone før tallene brukes til å vurdere enkeltpersoner.')}
                </li>
                <li>
                  {t('manager.modules.statistics.picking.info.cautionLowSample',
                    { '0': String(data?.parameters.minSampleLines ?? 50) },
                    `Merket «lavt utvalg» betyr færre enn ${data?.parameters.minSampleLines ?? 50} linjer i perioden — tilfeldige utslag gir da store forskjeller.`)}
                </li>
                <li>
                  {t('manager.modules.statistics.picking.info.cautionDeadTimeZone', {},
                    'Dødtid måles på tvers av hele lageret og påvirkes ikke av sonefilteret, selv når resten av tallene er filtrert.')}
                </li>
                <li>
                  {t('manager.modules.statistics.picking.info.cautionCodeChange', {},
                    'Markøren «kodeskifte» i grafen betyr at operatørkodene ble byttet ut på det tidspunktet. Ingen enkeltoperatør kan følges over et slikt brudd.')}
                </li>
              </ul>
            </div>

            <p className="text-xs text-gray-600">
              {t('manager.modules.statistics.picking.info.settingsNote', {},
                'Grenseverdiene kan endres per system og overstyres per profil via innstillingene PickingStatsWorkLimitMinutes, PickingStatsUpperLimitMinutes, PickingStatsDeadTimeGapMinutes og PickingStatsMinSampleLines.')}
            </p>
          </div>
        )}
      </div>

      {/* Kontroller */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('manager.modules.statistics.picking.interval', {}, 'Trendintervall')}
            </label>
            <select
              value={interval}
              onChange={(e) => setInterval(Number(e.target.value) as TrendInterval)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value={0}>{t('manager.modules.statistics.picking.intervalDay', {}, 'Dag')}</option>
              <option value={1}>{t('manager.modules.statistics.picking.intervalWeek', {}, 'Uke')}</option>
              <option value={2}>{t('manager.modules.statistics.picking.intervalMonth', {}, 'Måned')}</option>
            </select>
          </div>

          <div className="flex-1 min-w-[220px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('manager.modules.statistics.picking.zone', {}, 'Sone')}
            </label>
            <select
              multiple={false}
              value={zones[0] ?? ''}
              onChange={(e) => setZones(e.target.value ? [e.target.value] : [])}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">
                {t('manager.modules.statistics.picking.allZones', { '0': String(data?.availableZones.length ?? 0) },
                  `Alle soner (${data?.availableZones.length ?? 0})`)}
              </option>
              {(data?.availableZones ?? []).map(z => (
                <option key={z} value={z}>{z}</option>
              ))}
            </select>
          </div>

          <div className="ml-auto flex gap-2">
            <button
              onClick={fetchData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              {t('common.actions.refresh', {}, 'Oppdater')}
            </button>
            <button
              onClick={handleExport}
              disabled={loading || !data || data.rows.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              {t('common.actions.export', {}, 'Eksporter')}
            </button>
          </div>
        </div>
      </div>

      {/* Parameterstripe — grensene avgjør nesten hele resultatet, så de skal være synlige */}
      {data && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-gray-700 flex flex-wrap items-center gap-x-6 gap-y-2">
          {(() => {
            const totalEvaluated = data.totals.lines + data.parameters.excludedLines + data.parameters.discardedLines;
            const pct = totalEvaluated > 0 ? Math.round((data.totals.lines / totalEvaluated) * 100) : 0;
            return (
              <span className="font-semibold text-gray-800">
                {t('manager.modules.statistics.picking.coverage',
                  { '0': num(data.totals.lines), '1': num(totalEvaluated), '2': String(pct) },
                  `Grunnlag: ${num(data.totals.lines)} av ${num(totalEvaluated)} linjer (${pct} %) inngår`)}
              </span>
            );
          })()}
          <span>
            <span className="text-gray-500">{t('manager.modules.statistics.picking.workLimit', {}, 'Arbeidsgrense')}:</span>{' '}
            <span className="font-semibold tabular-nums">{data.parameters.workLimitMinutes} min</span>
          </span>
          <span>
            <span className="text-gray-500">{t('manager.modules.statistics.picking.upperLimit', {}, 'Øvre grense')}:</span>{' '}
            <span className="font-semibold tabular-nums">{data.parameters.upperLimitMinutes} min</span>
          </span>
          <span>
            <span className="text-gray-500">{t('manager.modules.statistics.picking.deadGap', {}, 'Maks dødtid-gap')}:</span>{' '}
            <span className="font-semibold tabular-nums">{data.parameters.deadTimeGapMinutes} min</span>
          </span>
          <span>
            <span className="text-gray-500">{t('manager.modules.statistics.picking.excluded', {}, 'Avbrutt')}:</span>{' '}
            <span className="font-semibold tabular-nums">{num(data.parameters.excludedLines)}</span>{' '}
            {t('manager.modules.statistics.picking.linesUnit', {}, 'linjer')}
          </span>
          {data.parameters.discardedLines > 0 && (
            <span>
              <span className="text-gray-500">{t('manager.modules.statistics.picking.discarded', {}, 'Forkastet')}:</span>{' '}
              <span className="font-semibold tabular-nums">{num(data.parameters.discardedLines)}</span>
            </span>
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">{t('common.status.error', {}, 'Feil')}</p>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {loading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
          <div className="flex flex-col items-center justify-center gap-4">
            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
            <p className="text-gray-600">{t('manager.modules.statistics.messages.loading', {}, 'Henter statistikk...')}</p>
          </div>
        </div>
      )}

      {!loading && data && data.rows.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center text-gray-600">
          {t('manager.modules.statistics.messages.noData', {}, 'Ingen data for valgt periode')}
        </div>
      )}

      {!loading && data && data.rows.length > 0 && (
        <>
          {/* Totalt for hele lageret + trend */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden lg:grid lg:grid-cols-[300px_1fr]">
            <div className="p-5 bg-gray-50 border-b lg:border-b-0 lg:border-r border-gray-200 space-y-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                {t('manager.modules.statistics.picking.totalAll', {}, 'Totalt · alle operatører')}
              </h3>
              <div>
                <div className="text-4xl font-bold text-blue-700 tabular-nums leading-none">
                  {num(data.totals.linesPerHour, 1)}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {t('manager.modules.statistics.picking.lph', {}, 'linjer per time')}
                </div>
                {data.totals.trendPercent != null && (
                  <div className={`text-sm font-semibold mt-1 ${
                    data.totals.trendPercent >= 0 ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {data.totals.trendPercent >= 0 ? '▲ +' : '▼ −'}
                    {num(Math.abs(data.totals.trendPercent), 0)} %{' '}
                    {t('manager.modules.statistics.picking.overPeriod', {}, 'i perioden')}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  [num(data.totals.lines), t('manager.modules.statistics.picking.linesUnit', {}, 'linjer')],
                  [num(data.totals.orders), t('manager.modules.statistics.picking.ordersUnit', {}, 'ordrer')],
                  [num(data.totals.pickingHours, 1), t('manager.modules.statistics.picking.pickHours', {}, 'plukktimer')],
                  [num(data.totals.interruptedHours, 1), t('manager.modules.statistics.picking.interruptedHours', {}, 'timer avbrutt')],
                  [num(data.totals.unitsPerHour, 0), t('manager.modules.statistics.picking.uph', {}, 'enheter/time')],
                  [num(data.totals.averageMinutesPerLine, 1), t('manager.modules.statistics.picking.minPerLine', {}, 'min per linje')],
                  [data.totals.averageDeadTimeMinutes != null ? num(data.totals.averageDeadTimeMinutes, 1) : '–',
                    t('manager.modules.statistics.picking.deadTime', {}, 'min dødtid')],
                  [num(data.totals.operators), t('manager.modules.statistics.picking.operatorsUnit', {}, 'operatører')]
                ].map(([v, l], i) => (
                  <div key={i}>
                    <div className="text-lg font-semibold tabular-nums text-gray-900">{v}</div>
                    <div className="text-xs text-gray-500">{l}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-5 min-w-0">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">
                {t('manager.modules.statistics.picking.trendTitle', {}, 'Utvikling')}
              </h3>
              <ResponsiveContainer width="100%" height={220}>
                <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                  <YAxis yAxisId="vol" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="lph" orientation="right" tick={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(value, name) => [
                      num(value as number, name === 'lph' ? 1 : 0),
                      name === 'lph'
                        ? t('manager.modules.statistics.picking.lph', {}, 'linjer per time')
                        : t('manager.modules.statistics.picking.volume', {}, 'plukkvolum')
                    ]}
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: 8 }}
                  />
                  <Bar yAxisId="vol" dataKey="lines" fill="#dbeafe" radius={[2, 2, 0, 0]} />
                  {breakLabels.map(l => (
                    <ReferenceLine
                      key={l}
                      yAxisId="lph"
                      x={l}
                      stroke="#d97706"
                      strokeDasharray="3 3"
                      label={{
                        value: t('manager.modules.statistics.picking.codeChange', {}, 'kodeskifte'),
                        fontSize: 9,
                        fill: '#b45309',
                        position: 'insideTopRight'
                      }}
                    />
                  ))}
                  <Line yAxisId="lph" type="monotone" dataKey="lph" stroke="#2563eb" strokeWidth={2.2} dot={{ r: 2 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Tabell */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-blue-600 sticky top-0">
                  <tr>
                    {th('operatorName', t('manager.modules.statistics.picking.operator', {}, 'Operatør'), 'text-left')}
                    {th('lines', t('manager.modules.statistics.picking.linesCol', {}, 'Linjer'), 'text-right')}
                    {th('orders', t('manager.modules.statistics.picking.ordersCol', {}, 'Ordrer'), 'text-right')}
                    {th('quantityPicked', t('manager.modules.statistics.picking.qtyCol', {}, 'Plukket antall'), 'text-right')}
                    {th('averageMinutesPerLine', t('manager.modules.statistics.picking.avgMinCol', {}, 'Snitt plukktid'), 'text-right')}
                    {th('linesPerHour', t('manager.modules.statistics.picking.lphCol', {}, 'Linjer/time'), 'text-right')}
                    {th('trendPercent', t('manager.modules.statistics.picking.trendCol', {}, 'Trend'), 'text-right')}
                    {th('unitsPerHour', t('manager.modules.statistics.picking.uphCol', {}, 'Enheter/time'), 'text-right')}
                    {th('pickingHours', t('manager.modules.statistics.picking.hoursCol', {}, 'Plukktimer'), 'text-right')}
                    {th('interruptedHours', t('manager.modules.statistics.picking.interruptedCol', {}, 'Avbrutt'), 'text-right')}
                    {th('averageDeadTimeMinutes', t('manager.modules.statistics.picking.deadTimeCol', {}, 'Snitt dødtid'), 'text-right')}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedRows.map((r, i) => (
                    <tr key={r.operatorId} className={`${i % 2 === 1 ? 'bg-blue-50/40' : ''} ${r.lowSample ? 'text-gray-500' : ''}`}>
                      <td className="px-3 py-2 text-sm whitespace-nowrap">
                        <span className="font-semibold">{r.operatorName}</span>
                        {r.lowSample && (
                          <span className="ml-2 text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-300 rounded px-1.5 py-0.5">
                            {t('manager.modules.statistics.picking.lowSample', {}, 'lavt utvalg')}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-right tabular-nums">{num(r.lines)}</td>
                      <td className="px-3 py-2 text-sm text-right tabular-nums">{num(r.orders)}</td>
                      <td className="px-3 py-2 text-sm text-right tabular-nums">{num(r.quantityPicked)}</td>
                      <td className="px-3 py-2 text-sm text-right tabular-nums">{num(r.averageMinutesPerLine, 1)}</td>
                      <td className="px-3 py-2 text-sm text-right tabular-nums font-semibold">{num(r.linesPerHour, 1)}</td>
                      <td className="px-3 py-2 text-sm text-right whitespace-nowrap">
                        <Sparkline series={r.trendSeries} percent={r.trendPercent} />
                      </td>
                      <td className="px-3 py-2 text-sm text-right tabular-nums">{num(r.unitsPerHour, 0)}</td>
                      <td className="px-3 py-2 text-sm text-right tabular-nums">{num(r.pickingHours, 1)}</td>
                      <td className="px-3 py-2 text-sm text-right tabular-nums">{num(r.interruptedHours, 1)}</td>
                      <td className="px-3 py-2 text-sm text-right tabular-nums">
                        {r.averageDeadTimeMinutes != null ? (
                          <>
                            {num(r.averageDeadTimeMinutes, 1)}
                            <span className="ml-1 text-xs text-gray-400">n={num(r.deadTimeSamples)}</span>
                          </>
                        ) : (
                          <span className="text-gray-400">–</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                  <tr className="font-semibold">
                    <td className="px-3 py-2 text-sm">
                      {t('manager.modules.statistics.picking.totalRow', { '0': String(data.totals.operators) },
                        `Totalt · ${data.totals.operators} operatører`)}
                      <span className="block text-[11px] font-normal text-gray-500">
                        {t('manager.modules.statistics.picking.weightedNote', {},
                          'vektet: sum linjer ÷ sum timer, ikke snitt av kolonnen')}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm text-right tabular-nums">{num(data.totals.lines)}</td>
                    <td className="px-3 py-2 text-sm text-right tabular-nums">{num(data.totals.orders)}</td>
                    <td className="px-3 py-2 text-sm text-right tabular-nums">{num(data.totals.quantityPicked)}</td>
                    <td className="px-3 py-2 text-sm text-right tabular-nums">{num(data.totals.averageMinutesPerLine, 1)}</td>
                    <td className="px-3 py-2 text-sm text-right tabular-nums">{num(data.totals.linesPerHour, 1)}</td>
                    <td className="px-3 py-2 text-sm text-right text-gray-500">
                      {data.totals.trendPercent != null
                        ? `${data.totals.trendPercent >= 0 ? '▲ +' : '▼ −'}${num(Math.abs(data.totals.trendPercent), 0)} %`
                        : '–'}
                    </td>
                    <td className="px-3 py-2 text-sm text-right tabular-nums">{num(data.totals.unitsPerHour, 0)}</td>
                    <td className="px-3 py-2 text-sm text-right tabular-nums">{num(data.totals.pickingHours, 1)}</td>
                    <td className="px-3 py-2 text-sm text-right tabular-nums">{num(data.totals.interruptedHours, 1)}</td>
                    <td className="px-3 py-2 text-sm text-right tabular-nums">
                      {data.totals.averageDeadTimeMinutes != null ? (
                        <>
                          {num(data.totals.averageDeadTimeMinutes, 1)}
                          <span className="ml-1 text-xs font-normal text-gray-400">n={num(data.totals.deadTimeSamples)}</span>
                        </>
                      ) : '–'}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

          </div>
        </>
      )}
    </div>
  );
};

export default PickingStatisticsView;
